import * as React from "react";
import useDebounce from "Hooks/useDebounce";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import styled from "styled-components";

type Props = {
  placeholder?: string;
  searchValue?: string;
  onSearch: (searchQuery: string) => void;
  debounceTime?: number;
  isFocused?: boolean;
  onKeyPressEnter?: (value: boolean) => void;
  onKeyPressEsc?: (value: boolean) => void;
  wasClickedSearchBtn: boolean;
};

const StyledWrapper = styled.div`
  input {
    background-color: #fff;
  }
`;

export const PrimaryDataTableSearch: React.FunctionComponent<Props> = props => {
  const {
    onSearch,
    onKeyPressEnter,
    onKeyPressEsc,
    searchValue,
    placeholder,
    debounceTime,
    isFocused,
    wasClickedSearchBtn,
  } = props;
  const [searchTerm, setSearchTerm] = React.useState("");
  const [enterKeyPress, setEnterKeyPress] = React.useState(false);
  const ref = React.useRef(null);
  const debouncedEnterKeyPress = useDebounce(
    enterKeyPress,
    debounceTime || 250,
  ) as boolean;

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setEnterKeyPress(!enterKeyPress);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" && onKeyPressEsc) {
      onKeyPressEsc(true);
    }
  };

  React.useEffect(() => {
    onSearch(searchTerm);
    onKeyPressEnter && onKeyPressEnter(!debouncedEnterKeyPress);
    // eslint-disable-next-line
  }, [debouncedEnterKeyPress, onSearch, onKeyPressEnter]);

  React.useEffect(() => {
    onSearch(searchTerm);
    // eslint-disable-next-line
  }, [wasClickedSearchBtn, onSearch]);

  React.useEffect(() => {
    if (isFocused) {
      const element = ref.current as HTMLElement | null;
      if (element) {
        element.focus();
      }
    }
  }, [isFocused]);

  React.useEffect(() => {
    if (searchValue !== searchTerm) {
      setSearchTerm(searchValue ?? "");
    }
    // eslint-disable-next-line
  }, [searchValue]);

  return (
    <StyledWrapper>
      <TextInput
        inputRef={ref}
        placeholder={placeholder}
        margin={"none"}
        fullWidth
        value={searchTerm}
        onChange={(e: { target: { value: React.SetStateAction<string> } }) =>
          setSearchTerm(e.target.value)
        }
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
      />
    </StyledWrapper>
  );
};
